import React, { useState, useEffect } from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';

export default function ThemeToggle() {
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('theme-preference') || 'system';
  });

  useEffect(() => {
    const root = document.documentElement;
    const applyTheme = (currentTheme) => {
      const isDark =
        currentTheme === 'dark' ||
        (currentTheme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);

      if (isDark) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
      // BRIDGE: keep the legacy [data-theme="night|day"] attribute in sync so the
      // existing global `filter: invert(1) hue-rotate(180deg)` rules on nav/main
      // (in index.css) continue to flip the entire UI. Without this the toggle
      // would only swap its own icon, not the page colors.
      root.setAttribute('data-theme', isDark ? 'night' : 'day');
      root.setAttribute('data-theme-mode', currentTheme);
    };

    applyTheme(theme);
    localStorage.setItem('theme-preference', theme);

    // Listen for system theme changes if in system mode
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (theme === 'system') {
        applyTheme('system');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme]);

  const cycleTheme = () => {
    setTheme((prev) => {
      if (prev === 'light') return 'dark';
      if (prev === 'dark') return 'system';
      return 'light';
    });
  };

  const renderIcon = () => {
    switch (theme) {
      case 'light':
        return <Sun size={18} />;
      case 'dark':
        return <Moon size={18} />;
      case 'system':
      default:
        return <Monitor size={18} />;
    }
  };

  return (
    <button
      onClick={cycleTheme}
      aria-label={`Current theme is ${theme}. Click to change.`}
      data-testid="theme-toggle"
      className="
        relative flex items-center justify-center w-10 h-10 rounded-full
        transition-all duration-300 ease-in-out
        text-foreground bg-transparent
        hover:bg-foreground hover:text-background
        active:scale-95
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2
        shadow-[0_0_8px_rgba(0,0,0,0.1)] dark:shadow-[0_0_12px_rgba(255,255,255,0.15)]
      "
      title={`Theme: ${theme}`}
    >
      {renderIcon()}
    </button>
  );
}
